import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ShoppingCart, Package, ArrowUpDown, Search, Loader2 } from "lucide-react";
import axios from "axios";
import { formatPrice, IMAGES } from "../mock";
import { useCart } from "../context/CartContext";
import { FeaturesBar } from "../components/Footer";
import PaintEdges from "../components/PaintEdges";
import ProductGallery from "../components/ProductGallery";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Produkte = () => {
  const { add, setOpen } = useCart();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [cat, setCat] = useState("Alle");
  const [sort, setSort] = useState("neueste");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/products`);
        if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) toast.error("Produkte konnten nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(
    () => ["Alle", ...new Set(items.map((p) => p.category).filter(Boolean))],
    [items]
  );

  const filtered = useMemo(() => {
    let list = [...items];
    if (cat !== "Alle") list = list.filter((p) => p.category === cat);
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.variant.toLowerCase().includes(needle) ||
          p.description.toLowerCase().includes(needle)
      );
    }
    if (sort === "preis-auf") list.sort((a, b) => a.price - b.price);
    if (sort === "preis-ab") list.sort((a, b) => b.price - a.price);
    if (sort === "neueste") list.sort((a, b) => (a.badge === "Neu" ? -1 : 0) - (b.badge === "Neu" ? -1 : 0));
    return list;
  }, [items, cat, q, sort]);

  const addToCart = (p) => {
    add(p);
    toast.success(`${p.name} – ${p.variant} wurde zum Warenkorb hinzugefügt`, {
      action: { label: "Ansehen", onClick: () => setOpen(true) },
    });
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#e9f7dd] via-[#fff4e3] to-white">
        <PaintEdges from="#63b93c" via="#a5cf6a" to="#f7941e" />
        <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="fade-up font-script text-2xl sm:text-3xl text-slate-700">
              Deine Idee. <span className="text-[#f7941e] underline decoration-wavy underline-offset-4">Unsere Umsetzung.</span>
            </p>
            <h1 className="fade-up-1 mt-2 font-display font-extrabold leading-[1.02] text-[clamp(2.4rem,5.5vw,4.2rem)] tracking-tight">
              <span className="text-[#3f8f2a]">FERTIGE</span>
              <br />
              <span className="text-[#f7941e]">3D-PRODUKTE</span>
            </h1>
            <p className="fade-up-2 mt-3 font-bold text-slate-700">
              Sofort verfügbar. Hochwertig gedruckt. Direkt bestellbar.
            </p>
            <p className="fade-up-2 mt-2 max-w-xl text-slate-600 leading-relaxed">
              Entdecke unsere bereits gefertigten 3D-Prints – von Figuren, über Deko bis hin
              zu kreativen Unikaten. Schnell, sicher und mit Liebe zum Detail.
            </p>
            <div className="fade-up-3 mt-7">
              <a href="#produktliste" className="btn-gradient-warm">
                <Package className="h-5 w-5" /> Produkte entdecken <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
          <div className="relative fade-up-2">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-tr from-lime-200/60 via-white to-amber-200/60 blur-xl" />
            <img
              src={IMAGES.figureShelf}
              alt="Fertige 3D-Produkte"
              className="relative rounded-[2rem] shadow-2xl shadow-lime-200/60 w-full h-[300px] sm:h-[380px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Filter */}
      <section id="produktliste" className="mx-auto max-w-[1440px] px-4 sm:px-8 py-8">
        {q && (
          <div className="mb-5 flex items-center gap-3 rounded-2xl bg-sky-50 border border-sky-100 px-5 py-3">
            <p className="text-slate-700">
              Suche nach: <span className="font-bold text-[#1b7fc1]">„{q}“</span>
            </p>
            <button
              onClick={() => setParams({})}
              className="ml-auto text-sm font-bold text-[#1b9ad6] hover:underline"
            >
              Zurücksetzen
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full px-6 py-2.5 font-bold text-sm transition-all duration-300 ${
                cat === c
                  ? "text-white bg-gradient-to-r from-[#63b93c] to-[#3f8f2a] shadow-md shadow-lime-200"
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
            >
              {c}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {})}
                placeholder="Produkte suchen ..."
                className="input-soft !rounded-full !pl-10 h-11 w-[200px]"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[190px] rounded-full border-slate-200 font-semibold">
                <ArrowUpDown className="h-4 w-4 mr-1 text-[#63b93c]" />
                <SelectValue placeholder="Sortieren" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="neueste">Neueste zuerst</SelectItem>
                <SelectItem value="preis-auf">Preis aufsteigend</SelectItem>
                <SelectItem value="preis-ab">Preis absteigend</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Produktliste */}
        {loading ? (
          <div className="mt-12 flex justify-center" data-testid="produkte-loading">
            <Loader2 className="h-8 w-8 text-[#3f8f2a] animate-spin" />
          </div>
        ) : (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {filtered.map((p) => (
            <article key={p.id} className="card-soft flex flex-col sm:flex-row">
              <div className="relative sm:w-[240px] shrink-0">
                <ProductGallery product={p} dim={!p.inStock} />
                <span
                  className={`absolute top-3 left-3 rounded-full px-3.5 py-1 text-xs font-extrabold text-white shadow ${
                    p.badge === "Neu" ? "bg-[#f7941e]" : "bg-[#3f8f2a]"
                  }`}
                >
                  {p.badge}
                </span>
                {!p.inStock && (
                  <span className="absolute top-3 right-3 rounded-full px-3.5 py-1 text-xs font-extrabold text-white shadow bg-slate-600">
                    Ausverkauft
                  </span>
                )}
              </div>
              <div className="flex-1 p-5 flex flex-col">
                <h3 className="font-display text-xl font-extrabold text-slate-800">
                  {p.name} <span className="text-slate-400">–</span>{" "}
                  <span className="text-[#f7941e]">{p.variant}</span>
                </h3>
                <p className="font-display text-2xl font-extrabold text-[#f7941e] mt-1">
                  {formatPrice(p.price)}
                </p>
                {p.inStock && p.stock <= 3 && (
                  <p className="text-sm font-bold text-[#e8760a]" data-testid={`product-low-stock-${p.id}`}>
                    Nur noch {p.stock} Stück verfügbar
                  </p>
                )}
                <p className="mt-2 text-slate-600 text-[15px] leading-relaxed flex-1">
                  {p.description}
                </p>
                {p.inStock ? (
                  <button onClick={() => addToCart(p)} className="btn-gradient-warm mt-4 w-full">
                    <ShoppingCart className="h-5 w-5" /> In den Warenkorb <ArrowRight className="h-5 w-5" />
                  </button>
                ) : (
                  <button
                    disabled
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full h-12 font-bold text-[15px] bg-slate-100 text-slate-400 cursor-not-allowed"
                  >
                    Derzeit nicht verfügbar
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="font-display text-2xl font-bold text-slate-700">Keine Produkte gefunden</p>
            <p className="text-slate-500 mt-2">Versuche es mit einem anderen Suchbegriff oder Filter.</p>
          </div>
        )}
      </section>

      <FeaturesBar />
    </main>
  );
};

export default Produkte;
